import { formatCurrency, formatDate } from '@if/core/utils'
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import { EasyCareComputeResultDto } from '@src/data/types/EasyCareWorksheetSchema'
import { DateOnly } from '@if/core/date'
import { EPaymentFrequency } from '@if/core/types'
import { useTranslation } from 'react-i18next'

interface Props {
  computedInfo: EasyCareComputeResultDto | null
  includeVehicleAssistance: boolean
  includeLoanProtection: boolean
  paymentFrequency: EPaymentFrequency
  firstPaymentDate: DateOnly | null
  isFetching?: boolean
}

const SummaryLine = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography>{label} :</Typography>
    <Typography>{value}</Typography>
  </Stack>
)

const EasyCareFundingSummary = ({
  computedInfo,
  includeVehicleAssistance,
  includeLoanProtection,
  paymentFrequency,
  firstPaymentDate,
  isFetching = false,
}: Props) => {
  const { t } = useTranslation()

  return (
    <Box sx={{ border: 1, padding: 2, height: 'fit-content', maxWidth: { md: '25rem' }, minWidth: '25rem' }}>
      <Stack direction="column" spacing={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          {t('worksheetCommon.fundingSummary')} :
        </Typography>

        {isFetching && (
          <div style={{ textAlign: 'center' }}>
            <CircularProgress />
          </div>
        )}

        {!isFetching && !computedInfo && <Typography>{t('easyCare.selectAProduct')}</Typography>}

        {!isFetching && computedInfo && (
          <>
            {includeVehicleAssistance && (
              <SummaryLine
                label={t('easyCare.vehicleAssistance')}
                value={formatCurrency(computedInfo.vehicleAssistanceAmount)}
              />
            )}

            {includeLoanProtection && (
              <SummaryLine
                label={t('easyCare.loanProtection')}
                value={formatCurrency(computedInfo.loanProtectionAmount)}
              />
            )}

            <Divider />

            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight="bold">{t('worksheetCommon.loanAmount')} :</Typography>
              <Typography fontWeight="bold">{formatCurrency(computedInfo.totalAmountFinanced)}</Typography>
            </Stack>

            <SummaryLine
              label={t('easyCare.loanTerm')}
              value={t('easyCare.months', { count: computedInfo.term })}
            />
            <SummaryLine
              label={t('worksheetCommon.annualPercentageRate')}
              value={`${computedInfo.interestRate} %`}
            />

            <Stack direction="row" justifyContent="space-between" paddingTop={3}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('worksheetCommon.payment')}
              </Typography>
            </Stack>

            <SummaryLine
              label={t('worksheetCommon.paymentFrequency')}
              value={t(`EPaymentFrequency.${paymentFrequency}`, { ns: 'enum' })}
            />
            <SummaryLine
              label={t('worksheetCommon.frequencyAmount')}
              value={formatCurrency(computedInfo.paymentAmount)}
            />
            <SummaryLine
              label={t('worksheetCommon.firstPaymentDate')}
              value={firstPaymentDate ? formatDate(firstPaymentDate.toString()) : ''}
            />
          </>
        )}
      </Stack>
    </Box>
  )
}

export default EasyCareFundingSummary
